import React, { useState, useEffect } from 'react';

/**
 * StaffPerformanceView - Detailed view of individual staff member performance
 *
 * Features:
 * - Overview metrics (requests handled, closed, avg time, satisfaction)
 * - Outcome breakdown (connected, declined, expired, withdrawn)
 * - Performance trend chart
 * - Active workload table
 * - Request type breakdown
 * - Channel effectiveness comparison
 */

const StaffPerformanceView = ({
  staffId,
  staffName = 'Staff Member',
  onClose,
  darkMode = false
}) => {
  const [metrics, setMetrics] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    fetchMetrics();
  }, [staffId, dateRange]);

  const fetchMetrics = async () => {
    if (!staffId) return;

    setLoading(true);
    setError(null);

    try {
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - parseInt(dateRange));

      const response = await fetch(
        `/api/staff/brokerage/analytics/staff/${staffId}?date_from=${dateFrom.toISOString().split('T')[0]}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch staff metrics');
      }

      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate percentage change
  const calcChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Format days
  const formatDays = (days) => {
    if (!days && days !== 0) return 'N/A';
    return `${days.toFixed(1)}d`;
  };

  // Render metric card
  const MetricCard = ({ title, value, change, suffix = '', format = 'default' }) => {
    const formattedValue = format === 'days' ? formatDays(value)
      : format === 'percent' ? `${(value * 100).toFixed(1)}%`
      : format === 'rating' ? `${value?.toFixed(1) || 'N/A'}/5`
      : value;

    return (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
        <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {formattedValue}{suffix}
        </p>
        {change !== null && change !== undefined && (
          <p className={`text-xs mt-1 ${
            parseFloat(change) > 0 ? 'text-green-500' : parseFloat(change) < 0 ? 'text-red-500' : 'text-gray-400'
          }`}>
            {parseFloat(change) > 0 ? '▲' : parseFloat(change) < 0 ? '▼' : '–'} {Math.abs(change)}% vs previous
          </p>
        )}
      </div>
    );
  };

  // Render progress bar
  const ProgressBar = ({ value, max, label, color = 'indigo' }) => {
    const percentage = max > 0 ? (value / max * 100) : 0;
    return (
      <div className="mb-2">
        <div className="flex justify-between text-sm mb-1">
          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{label}</span>
          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{value}</span>
        </div>
        <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
          <div
            className={`h-2 rounded-full bg-${color}-500`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${darkMode ? 'bg-black/70' : 'bg-black/50'}`}>
        <div className={`p-8 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Loading performance data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${darkMode ? 'bg-black/70' : 'bg-black/50'}`}>
      <div className={`w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-indigo-900' : 'bg-indigo-100'}`}>
              <span className="text-indigo-500 font-bold">{staffName.charAt(0)}</span>
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {staffName}
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Staff Performance
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className={`px-3 py-1 rounded-lg border text-sm ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {error ? (
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
              <p className="text-red-500">{error}</p>
            </div>
          ) : metrics ? (
            <>
              {/* Overview Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Requests Handled"
                  value={metrics.total_handled || 0}
                  change={calcChange(metrics.total_handled, metrics.previous_total_handled)}
                />
                <MetricCard
                  title="Closed Successfully"
                  value={metrics.closed_successfully || 0}
                  suffix={metrics.total_handled ? ` (${((metrics.closed_successfully / metrics.total_handled) * 100).toFixed(0)}%)` : ''}
                />
                <MetricCard
                  title="Avg Time to Close"
                  value={metrics.avg_time_to_close}
                  format="days"
                  change={calcChange(metrics.previous_avg_time, metrics.avg_time_to_close)}
                />
                <MetricCard
                  title="Satisfaction Rating"
                  value={metrics.satisfaction_rating}
                  format="rating"
                  change={calcChange(metrics.satisfaction_rating, metrics.previous_satisfaction)}
                />
              </div>

              {/* Two Column Layout */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Outcome Breakdown */}
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                  <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Outcome Breakdown
                  </h3>
                  <div className="space-y-3">
                    <ProgressBar
                      label="Connected"
                      value={metrics.outcome_breakdown?.connected || 0}
                      max={metrics.total_handled || 1}
                      color="green"
                    />
                    <ProgressBar
                      label="Declined"
                      value={metrics.outcome_breakdown?.declined || 0}
                      max={metrics.total_handled || 1}
                      color="red"
                    />
                    <ProgressBar
                      label="Expired"
                      value={metrics.outcome_breakdown?.expired || 0}
                      max={metrics.total_handled || 1}
                      color="amber"
                    />
                    <ProgressBar
                      label="Withdrawn"
                      value={metrics.outcome_breakdown?.withdrawn || 0}
                      max={metrics.total_handled || 1}
                      color="gray"
                    />
                  </div>
                </div>

                {/* Request Type Breakdown */}
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                  <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Request Type Breakdown
                  </h3>
                  <div className="space-y-3">
                    {metrics.requests_by_type ? Object.entries(metrics.requests_by_type).map(([type, count]) => (
                      <ProgressBar
                        key={type}
                        label={type.replace(/_/g, ' ')}
                        value={count}
                        max={metrics.total_handled || 1}
                        color="indigo"
                      />
                    )) : (
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Channel Effectiveness */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Channel Effectiveness
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {metrics.channel_effectiveness ? Object.entries(metrics.channel_effectiveness).map(([channel, data]) => (
                    <div key={channel} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                      <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {channel.charAt(0).toUpperCase() + channel.slice(1)}
                      </p>
                      <p className={`text-2xl font-bold mt-1 ${
                        (data.response_rate || 0) >= 0.7 ? 'text-green-500' :
                        (data.response_rate || 0) >= 0.5 ? 'text-amber-500' : 'text-red-500'
                      }`}>
                        {((data.response_rate || 0) * 100).toFixed(0)}%
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        response rate
                      </p>
                    </div>
                  )) : (
                    <p className={`text-sm col-span-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No channel data available</p>
                  )}
                </div>
              </div>

              {/* Active Workload */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Active Workload ({metrics.active_workload?.length || 0} requests)
                </h3>
                {metrics.active_workload?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                          <th className="text-left py-2">Request ID</th>
                          <th className="text-left py-2">Type</th>
                          <th className="text-left py-2">Status</th>
                          <th className="text-left py-2">Age</th>
                          <th className="text-left py-2">Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.active_workload.map(item => (
                          <tr key={item.id} className={`border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                            <td className={`py-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {item.id?.slice(0, 8)}...
                            </td>
                            <td className={`py-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {({'PEER_MID_LEVEL':'Peer Networking','PEER_SENIOR':'Peer Networking (Senior)','MENTORSHIP_UP':'Seek Mentorship','MENTORSHIP_HIGH':'Offer Mentorship','BOARD_PATHWAY':'Board Pathway','HBCU_MENTORSHIP':'HBCU Mentorship'})[item.request_type] || item.request_type?.replace(/_/g, ' ')}
                            </td>
                            <td className="py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                item.status === 'IN_REVIEW' ? 'bg-blue-100 text-blue-700' :
                                item.status === 'STAGED' ? 'bg-purple-100 text-purple-700' :
                                item.status === 'AWAITING' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {item.status}
                              </span>
                            </td>
                            <td className={`py-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {item.age_days || 0}d
                            </td>
                            <td className="py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                item.priority === 'high' ? 'bg-red-100 text-red-700' :
                                item.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {item.priority || 'normal'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No active assignments
                  </p>
                )}
              </div>

              {/* Performance Trend (Simplified - would use chart library in production) */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Performance Trend
                </h3>
                {metrics.trend_data?.length > 0 ? (
                  <div className="h-32 flex items-end gap-1">
                    {metrics.trend_data.map((point, idx) => (
                      <div
                        key={idx}
                        className="flex-1 bg-indigo-500 rounded-t"
                        style={{ height: `${Math.max((point.value / Math.max(...metrics.trend_data.map(p => p.value))) * 100, 5)}%` }}
                        title={`${point.date}: ${point.value} requests`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className={`h-32 flex items-center justify-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <p className="text-sm">No trend data available for this period</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>No performance data available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex justify-end p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffPerformanceView;
